import ChatMessage from '@components/messages/ChatMessage';
import type { Message } from '@hooks/useMessage';
import clsx from 'clsx';
import { format, isToday, isYesterday } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ThreadMessageListProps {
	messages?: Message[];
}

const ThreadMessageList = ({ messages }: ThreadMessageListProps) => {
	const { t } = useTranslation('', { keyPrefix: 'messages' });
	const messageContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (messageContainerRef.current) {
			messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
		}
	}, [messages]);

	const getDateLabel = (dateStr: string) => {
		const date = new Date(dateStr);
		if (isToday(date)) {
			return t('form.today');
		}
		if (isYesterday(date)) {
			return t('form.yesterday');
		}
		return format(date, 'dd MMM yyyy');
	};

	return (
		<div ref={messageContainerRef} className="sm:px-space-16 flex-1">
			{messages?.map((msg, index) => {
				const currentLabel = getDateLabel(msg.created_at);
				const prevLabel = index > 0 ? getDateLabel(messages[index - 1].created_at) : null;

				const showLabel = currentLabel !== prevLabel;

				return (
					<div key={msg.uuid}>
						{showLabel && (
							<span
								className={clsx(
									'text-center text-sm-bold mb-space-16 block',
									index > 0 && 'border-t border-neutral-100 pt-space-32 mt-space-32',
								)}
							>
								{currentLabel}
							</span>
						)}
						<ChatMessage
							content={msg.content}
							createdAt={msg.created_at}
							files={msg?.attachments}
							isSelf={msg.sender_type === 'user'}
						/>
					</div>
				);
			})}
		</div>
	);
};

export { ThreadMessageList };
